<template>
    <div class="choose-exam">
        <BundleSearch
            :bundles="bundles"
            :exam-metadata-by-id="examMetadata"
            @select="handleSelectExam"
            @selectBundle="handleSelectBundle"
            @search="handleSearch"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import type { TBundleExam } from '@/components/Register/ChooseExam/BundleExam.vue'
import type { Study } from '@pocketprep/types'
import { bundleModule } from '@/store/bundle/module'
import { examModule } from '@/store/exam/module'

const { BundleSearch } = UIKit

const emit = defineEmits<{
    select: [exam: TBundleExam]
    selectBundle: [bundle: Study.Class.BundleJSON]
    search: [searchString: string]
}>()

const bundles = computed(() => {
    const b = bundleModule.getters.getBundles()
    const allBundles = Array.isArray(b) ? b : (b ? Object.values(b) : [])
    return allBundles.filter(bundle => bundle.name !== 'Pocket Prep')
})

const examMetadata = computed(() => {
    return examModule.getters.getUniqueExamMetadata({ excludeDiscontinued: true })
})

const handleSelectExam = (exam: TBundleExam) => {
    emit('select', exam)
}

const handleSelectBundle = (bundle: Study.Class.BundleJSON) => {
    emit('selectBundle', bundle)
}

const handleSearch = (search: string) => {
    emit('search', search)
}

onMounted(async () => {
    await Promise.all([
        bundleModule.actions.fetchBundles(),
        examModule.actions.fetchExamMetadata(),
    ])
})
</script>

<style lang="scss" scoped>
.choose-exam {
    padding-top: 23px;
    max-width: 885px;
    width: 100%;
    height: calc(100% - 80px);
    margin: 0 auto;
    box-sizing: border-box;

    &__phone-person {
        position: absolute;
        right: 0;
        bottom: 0;
        width: 275px;
    }

    &__select-exam-container {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    &__search {
        width: 250px;
        margin-bottom: 41px;
    }

    &__search-results {
        width: 305px;
    }

    &__search-results-header {
        box-sizing: border-box;
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 305px;
        padding-left: 20px;
        padding-right: 26px;
    }

    &__search-results-count {
        font-size: 13px;
        line-height: 17.76px;
        color: $slate-03;
    }

    &__search-reset-btn {
        font-size: 13px !important;
        line-height: 17.76px !important;
    }

    &__search-results-list {
        padding: 0;
        margin: 0;
        height: 340px;
        list-style-type: none;
        display: flex;
        flex-wrap: wrap;
        align-content: flex-start;
        overflow: auto;
    }

    &__browse-industry-text {
        font-weight: 600;
        margin-bottom: 12px;
        font-size: 13px;
        line-height: 17.76px;
        padding-left: 5px;
    }

    &__bundle-search {
        display: flex;
    }

    &__bundle-list {
        box-sizing: border-box;
        list-style-type: none;
        margin: 0 0 38px 0;
        padding: 0;
    }

    &__bundle {
        display: flex;
        align-items: center;
        font-size: 14px;
        letter-spacing: 0.2px;
        line-height: 19px;
        white-space: nowrap;
        outline: none;
        cursor: pointer;
    }

    &__bundle-icon {
        width: 25px;
        height: 25px;
        margin-right: 9px;
    }

    &__bundle-exam-container {
        display: flex;
        flex-direction: column;
        margin-left: 100px;
        width: 642px;
    }

    &__bundle-exam-count {
        font-size: 13px;
        letter-spacing: 0.2px;
        line-height: 17.76px;
        margin-left: 29px;
        margin-bottom: 10px;
        font-weight: 600;
    }

    &__bundle-exam-list {
        padding: 0;
        margin: 0;
        height: 340px;
        list-style-type: none;
        display: flex;
        align-content: flex-start;
        flex-wrap: wrap;
        overflow: auto;
    }

    &__exam-list-padding {
        width: 100%;
        height: 40px;
    }

    &__exam-confirmation {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 410px;
    }

    &__selected-exam-container {
        position: relative;
    }

    &__selected-exam {
        margin-bottom: 43px;
        cursor: auto;

        &:hover,
        &:focus {
            &::before {
                display: none;
            }
        }
    }

    &__unselect-exam-icon {
        position: absolute;
        top: 14px;
        right: 21px;
        color: $slate-01;
        cursor: pointer;

        &:hover {
            color: $brand-black;
        }
    }

    &__display-name-prompt {
        font-weight: 600;
        font-size: 15px;
        line-height: 20.49px;
        margin-bottom: 10px;
        padding-left: 11px;
    }

    &__display-name-hint {
        color: $slate-03;
        font-size: 14px;
        line-height: 19px;
        margin-bottom: 27px;
        padding-left: 11px;
    }

    &__display-name-input {
        margin-bottom: 41px;
    }

    &__submit {
        margin-bottom: 57px;
    }
}
</style>
